<template>
	<div class="">
		<div class="py-20 px-3">
			<div class="flex w-full justify-center">
				<ImageScroller class="m-auto block" label="You May Also Like" :products="products" />
			</div>
		</div>
	</div>
</template>

<script>
import Mango from '../../../helpers/mango'
import Stars from '../../partials/stars.vue'
// import Product from '../../products/product.vue'
import ImageScroller from './image-scroller.vue'

import { algoliaAppId, algoliaSearchKey, algoliaIndex } from '../../../../../config/config/settings.json'
import { algoliasearch } from 'algoliasearch'
import { fetchBoughtTogetherRecommendations, fetchRelatedRecommendations } from '../../../helpers/relatedRecommendations.js'

const recommend = algoliasearch(algoliaAppId, algoliaSearchKey).initRecommend()

export default {
	components: { ImageScroller, Stars },
	props: ['product'],
	inject: ['store'],
	data() {
		return {
			link: '/item-id',
			products: [],
		}
	},

	methods: {
		async getDynamicRelatedProducts() {
			if (!this.product) return []
			try {
				const TOTAL_RELATED_PRODUCTS = 12
				let relatedProducts = []

				// If brandId exists, get products from the same brand
				const brandId = this.product.brandId
				const categories = this.product.categories || []

				let brandProductsCount = 0
				let categoryProductsNeeded = TOTAL_RELATED_PRODUCTS

				// Step 1: Get products from the same brand if brandId exists
				if (brandId) {
					brandProductsCount = 4 // Default number of brand products to fetch

					// Get products from the same brand, excluding the current product
					const brandProducts = await this.fetchBrandProducts(brandId, brandProductsCount)

					// If we got fewer brand products than expected, adjust the categoryProductsNeeded
					brandProductsCount = brandProducts.length
					categoryProductsNeeded = TOTAL_RELATED_PRODUCTS - brandProductsCount

					// Add brand products to the relatedProducts array
					relatedProducts = [...brandProducts]
				}

				// Step 2: Get products from categories to complete the set
				if (categories.length > 0 && categoryProductsNeeded > 0) {
					const categoryProducts = await this.fetchCategoryProducts(categories, categoryProductsNeeded)
					relatedProducts = [...relatedProducts, ...categoryProducts]
				}

				// Return the combined list of related products, shuffled for randomness
				return this.shuffleArray(relatedProducts).slice(0, TOTAL_RELATED_PRODUCTS)
			} catch (error) {
				console.error('Error fetching dynamic related products:', error)
				return []
			}
		},

		async fetchBrandProducts(brandId, count) {
			try {
				// Generate a random skip value for randomization
				const randomSkip = Math.floor(Math.random() * 20)

				// Fetch products from the same brand, excluding the current product and out-of-stock items
				const brandProducts = await Mango.products({
					search: {
						categories: { $nin: ['66d8b83f6e195ee1d5e05680', '66d8b83f6e195ee1d5e0568a'] }, // exclude products from the birth kit categories
						brandId: brandId,
						_id: { $ne: this.product._id }, // Exclude the current product
						outOfStock: { $ne: true }, // Exclude products marked as out of stock
						isVisible: true,
					},
					limit: count + 25, // Fetch extra products to ensure we have enough after filtering out-of-stock items
					skip: randomSkip, // Skip a random number of products for randomization
					sort: { _id: Date.now() % 2 === 0 ? 1 : -1 }, // Random sort direction
				})

				// Further filter out products where all variants are out of stock
				const inStockProducts = brandProducts.filter((product) => {
					// If product doesn't track inventory by variant, it's already filtered by outOfStock above
					if (product.inventoryTracking !== 'variant') return true

					// Check if all variants are out of stock
					return !product.variants?.every((v) => v.inventoryLevel <= 0)
				})

				// Shuffle the results for randomness and limit to the requested count
				return this.shuffleArray(inStockProducts).slice(0, count)
			} catch (error) {
				console.error('Error fetching brand products:', error)
				return [] // Return empty array in case of error
			}
		},
		async fetchCategoryProducts(categoryObjects, totalNeeded) {
			const categories = categoryObjects.map((category) => category.id)
			try {
				// Generate a random skip value for randomization
				const randomSkip = Math.floor(Math.random() * 20)

				// Query the database once for all categories using $in operator
				// This is more efficient than querying each category separately
				const categoryProducts = await Mango.products({
					search: {
						categories: {
							$in: categories,
							$nin: ['66d8b83f6e195ee1d5e05680', '66d8b83f6e195ee1d5e0568a'], // exclude products from the birth kit categories
						},
						_id: { $ne: this.product._id }, // Exclude the current product
						outOfStock: { $ne: true }, // Exclude products marked as out of stock
						isVisible: true,
					},
					limit: totalNeeded + 35, // Fetch more extra products to account for filtering out-of-stock items
					skip: randomSkip, // Skip a random number of products for randomization
					sort: { _id: Date.now() % 2 === 0 ? 1 : -1 }, // Random sort direction
				})

				// Filter out products where all variants are out of stock
				const inStockCategoryProducts = categoryProducts.filter((product) => {
					// If product doesn't track inventory by variant, it's already filtered by outOfStock above
					if (product.inventoryTracking !== 'variant') return true

					// Check if all variants are out of stock
					return !product.variants?.every((v) => v.inventoryLevel <= 0)
				})

				// Shuffle the results for additional randomness
				let allCategoryProducts = this.shuffleArray(inStockCategoryProducts).slice(0, totalNeeded)

				// If we still do not have enough products, try a broader search
				if (allCategoryProducts.length < totalNeeded) {
					const remainingNeeded = totalNeeded - allCategoryProducts.length

					// Generate a different random skip for fallback products
					const randomFallbackSkip = Math.floor(Math.random() * 20)

					// Get random products (not from this product brand or categories)
					const randomProducts = await Mango.products({
						search: {
							_id: { $ne: this.product._id },
							outOfStock: { $ne: true }, // Exclude products marked as out of stock
							isVisible: true,
						},
						limit: remainingNeeded + 30, // Get more for better randomness and to account for filtering
						skip: randomFallbackSkip, // Skip a random number of products for randomization
					})

					// Filter out any products we have already added and any out-of-stock variant products
					const uniqueRandomProducts = randomProducts.filter((product) => {
						// First check if it's already in our list
						if (allCategoryProducts.some((p) => p._id === product._id)) return false

						// Then check variant inventory if applicable
						if (product.inventoryTracking === 'variant') {
							return !product.variants?.every((v) => v.inventoryLevel <= 0)
						}

						return true
					})

					// Add the unique random products
					allCategoryProducts = [...allCategoryProducts, ...this.shuffleArray(uniqueRandomProducts).slice(0, remainingNeeded)]
				}

				return allCategoryProducts
			} catch (error) {
				console.error('Error fetching category products:', error)
				return [] // Return empty array in case of error to prevent UI breaks
			}
		},

		shuffleArray(array) {
			// Fisher-Yates shuffle algorithm for randomizing array order
			const newArray = [...array]
			for (let i = newArray.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1))
				;[newArray[i], newArray[j]] = [newArray[j], newArray[i]]
			}
			return newArray
		},
		async getBoughtTogether(n = 8, t = 0) {
			return fetchBoughtTogetherRecommendations({
				recommend,
				indexName: algoliaIndex,
				product: this.product,
				maxRecommendations: n,
				threshold: t,
				onError: (error) => console.error('Error fetching bought together products:', error),
			})
		},
		async getRelated(n = 24, t = 0) {
			if (!this.product?.id) return
			console.log('getting related items...')
			const upsellItems = await fetchRelatedRecommendations({
				recommend,
				indexName: algoliaIndex,
				product: this.product,
				maxRecommendations: n,
				threshold: t,
				onError: (error) => console.error('Error fetching related products:', error),
			})
			console.log('upsellItems: ', upsellItems)
			this.products = upsellItems
		},
	},

	watch: {
		'store.cartInitialized': {
			immediate: true,
			handler() {
				if (this.store.cartInitialized) {
					this.getRelated()
				}
			},
		},
	},
}
</script>
